import { useAppSelector } from '@/store/hooks';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { selectReportingV2Filters } from '@/store/slices/reportingV2/reportingV2Slice';
import { ArrowDownwardRounded, ArrowUpwardRounded, UnfoldMoreRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  CONVERSIONS_LABEL,
  CONVERSIONS_NOT_ADDITIVE_NOTE,
  NOT_ADDITIVE_LABEL,
  compactCurrency,
  currency,
  dimensionLabel,
  isAdvertiserRollupNotAdditive,
  isAttributionRestricted,
  maskedChannelLabelFn,
  num,
  percent,
  withEntityDsp,
} from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { useReportWidget } from '../hooks/useReportWidget';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { COLORS, cardSx } from '../theme';
import ColumnResizeHandle from './ColumnResizeHandle';
import { CHIPS, pageWindow, resolve } from './ImpressionsByType';
import { PUBLISHER_DIMENSION } from './TopPublishersCard';
import WidgetExportButton from './WidgetExportButton';
import WidgetSkeleton from './WidgetSkeleton';

/** Row counts the selector offers (prep #0000 — settled on 20/50/100 in the
 * product meeting), default first. */
export const ROWS_PER_PAGE_OPTIONS = [20, 50, 100] as const;

/**
 * Client-side pagination for the Hierarchy/Channel/Geo/Publishers/Dimensions
 * tables below — all five Hierarchy sub-views (Advertiser, Campaign, Order,
 * Line Item, Creative) share this one render path, so fixing it here fixes
 * all of them. Previously every one of (up to 500) sorted rows rendered at
 * once with no pager, confirmed live rendering hundreds of rows on the
 * Advertiser view (prep #0000).
 *
 * `page` clamps to the last real page rather than throwing or returning
 * nothing — switching from 100 rows/page back down to 20 while on page 3 of
 * the smaller size must not strand the view on an empty page.
 */
export const paginateRows = <T,>(
  rows: T[],
  page: number,
  rowsPerPage: number,
): { pageRows: T[]; pageCount: number; safePage: number } => {
  const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const safePage = Math.min(Math.max(0, page), pageCount - 1);
  return { pageRows: rows.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage), pageCount, safePage };
};

/**
 * The raw (untruncated) value behind a hierarchy row's display name, for a
 * hover tooltip on names `labelFn` (`cleanName`/`orderLabel`) has already cut
 * at 60 chars, or that the cell's own CSS ellipsis has clipped further on a
 * long campaign name (prep #0000 — Martin: no way to see the full name).
 * Null when there's nothing more to show than the display name itself.
 */
export const fullNameFor = (row: ReportRow, label: string): string | null => {
  const raw = row[label];
  return raw === null || raw === undefined || raw === '' ? null : String(raw);
};

const darkToggleSx = {
  bgcolor: COLORS.pageBg,
  borderRadius: '10px',
  border: `1px solid ${COLORS.border}`,
  p: 0.5,
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    border: 0,
    borderRadius: '8px !important',
    px: 1.5,
    py: 0.25,
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 600,
    '&.Mui-selected': { bgcolor: COLORS.black, color: '#fff', '&:hover': { bgcolor: '#000' } },
  },
} as const;

/**
 * Same tab set as Impressions by Type (flank#4648 — "Tabs match Impressions by
 * Type"), relabeling 'inventory' to 'Publishers' per the ticket's naming. Its
 * own 'publisher' chip is wired to the `ribeye_publisher` rollup (see
 * isPublisherInventory below) — the App/Domain chips stay on the raw
 * breakdown as before (see the Publishers-tab note below the grid).
 */
const TABS = [
  { key: 'hierarchy', label: 'Hierarchy' },
  { key: 'channel', label: 'Channel' },
  { key: 'geo', label: 'Geo' },
  { key: 'inventory', label: 'Publishers' },
  { key: 'dimensions', label: 'Dimensions' },
];

/** Tabs whose chips resolve to dimensions that live only on GEOGRAPHY or
 * APP/DOMAIN — `conversion_type` is PERFORMANCE-only (dimensionRegistry.ts),
 * so a query combining it with geo_state/app_name/etc. 400s ("no common
 * source table"). These two tabs show combined (un-split) conversions
 * instead of gating the whole widget on it. */
const NOT_TYPE_SPLITTABLE = new Set(['geo', 'inventory']);

export type ConversionType = 'web' | 'listen_through' | 'foot_traffic';

/** Human labels for every type the warehouse can stamp — not every type is
 * always offered, see `availableTypes` below (review, Lucas: offer what the
 * data actually has, not a hardcoded Web/Foot-traffic pair). Exported for
 * Custom Dashboards' own conversion-type split (flank#5098), which mirrors
 * this page's "user picks the type" pattern rather than a separate vocabulary. */
export const CONVERSION_TYPE_LABELS: Record<ConversionType, string> = {
  web: 'Web',
  listen_through: 'Listen-through',
  foot_traffic: 'Foot traffic',
};

const defaultChipKey = (tab: string): string => {
  const chips = CHIPS[tab] ?? [];
  return (chips.find(c => c.dims) ?? chips[0])?.key ?? '';
};

const toNum = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const SORT_KEYS = ['__name', 'conversions', 'conv_rate', 'impressions', 'clicks', 'spend', 'cost_per_conv'] as const;
type SortKey = (typeof SORT_KEYS)[number];

// All three columns derived from `conversions` — a rolled-up Advertiser row
// spanning more than one platform makes conv_rate and cost_per_conv just as
// unreliable as the raw conversions sum (same attribution-overlap hazard
// CONVERSIONS_NOT_ADDITIVE_NOTE describes), so they're gated together.
const CONVERSIONS_DERIVED_COLS = new Set<SortKey>(['conversions', 'conv_rate', 'cost_per_conv']);

/**
 * The chips offered for a tab, with the Platform/DSP chip (dims: ['dsp'])
 * withheld unless the viewer can see DSP/Platform — the same gate every other
 * Platform surface in this codebase applies (ImpressionsByType's own
 * `chipsForTab`, flank#4650). Pure and exported so the gate is testable
 * without mounting the component (flank#4741 — this widget used to skip the
 * check entirely, letting its Channel tab's "Platform" chip render DSP names
 * and a per-platform breakdown regardless of the permission).
 */
export const platformFilteredChips = (tab: string, canViewPlatform: boolean) =>
  (CHIPS[tab] ?? []).filter(chip => canViewPlatform || !chip.dims?.includes('dsp'));

interface SortRowsOptions {
  label: string;
  idKey?: string;
  labelFn: (value: unknown, id?: unknown) => string;
}

/**
 * Sorts the FULL row set by `col` — this must run before `paginateRows` ever sees the
 * rows, or sorting would only re-order whatever happened to already be on the current
 * page instead of the whole dataset (flank#4742 AC2). Exported standalone, and taking
 * no page/pageSize, so the contract is unit-testable without mounting the widget: see
 * the "sort-then-paginate" case in Conversions.test.ts.
 */
export const sortRows = (
  rows: ReportRow[],
  col: SortKey,
  dir: 'asc' | 'desc',
  { label, idKey, labelFn }: SortRowsOptions,
): ReportRow[] => {
  const value = (row: ReportRow): number | string => {
    if (col === '__name') return labelFn(row[label], idKey ? row[idKey] : undefined);
    // A multi-platform Advertiser rollup row reads "Not additive" for these
    // three, not a real number — sorting by its hidden summed value put it
    // at the top of the default (conversions desc) sort (review, Lucas).
    if (CONVERSIONS_DERIVED_COLS.has(col) && isAdvertiserRollupNotAdditive(Object.keys(row), row)) {
      return -Infinity;
    }
    if (col === 'conv_rate') {
      const c = toNum(row.conversions);
      const i = toNum(row.impressions);
      return c !== null && i ? c / i : -Infinity;
    }
    if (col === 'cost_per_conv') {
      const s = toNum(row.spend);
      const c = toNum(row.conversions);
      return s !== null && c ? s / c : -Infinity;
    }
    return toNum(row[col]) ?? -Infinity;
  };
  const d = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = value(a);
    const bv = value(b);
    if (typeof av === 'string' || typeof bv === 'string') return d * String(av).localeCompare(String(bv));
    return d * (av - bv);
  });
};

/** The 'Publishers' chip under the 'Publishers' tab (ImpressionsByType.tsx's
 * CHIPS.inventory key is 'publisher', relabeled 'Publishers' for this tab's
 * own TABS array above) — the one chip this widget routes to the
 * `ribeye_publisher` rollup instead of the chip's own raw dims. */
export const isPublisherInventoryTab = (tab: string, activeSubTab: string): boolean =>
  tab === 'inventory' && activeSubTab === 'publisher';

interface ChipDims {
  dims: string[];
  label: string;
  idKey?: string;
  labelFn: (value: unknown, id?: unknown) => string;
}

/**
 * The 'publisher' chip's own `dims: ['app_name']` (ImpressionsByType.tsx's
 * CHIPS.inventory) only exists for the shared reach/conversions scope gate —
 * it was never meant to drive the actual query here, but this widget (unlike
 * ImpressionsByType, which special-cases this key entirely) just used it
 * directly, so this tab rendered the same raw, mostly-"Unnamed item" rows
 * #4643/#4656 already fixed everywhere else (Martin, 2026-10-12: "feels like
 * it was solved in impression by type, but not in conversions").
 *
 * Swapping to the `ribeye_publisher` rollup fixes it the same way — no
 * ribeye-api change needed, `runReport` already dispatches any caller
 * requesting this dimension to `runPublisherRollupReport` regardless of which
 * metrics it asks for alongside it. `ribeye_publisher_name` already IS the
 * display string (a real publisher name, "Unknown / Pending mapping", or
 * "Unmapped") — entityLabel/maskedChannelLabelFn exist to clean up a RAW
 * dimension value, which this isn't, and GAM-prefix masking doesn't apply to
 * a publisher name either.
 */
export const resolveConversionsDims = (isPublisherInventory: boolean, chip: ChipDims): ChipDims =>
  isPublisherInventory
    ? {
        dims: [PUBLISHER_DIMENSION],
        label: 'ribeye_publisher_name',
        idKey: PUBLISHER_DIMENSION,
        labelFn: (value: unknown) => String(value ?? 'Unmapped'),
      }
    : chip;

const Conversions = () => {
  const [convType, setConvType] = useState<ConversionType>('web');
  const [tab, setTab] = useState('hierarchy');
  const { widths: colWidths, startResize } = useResizableColumns(`conversions.${tab}`);
  const [subTab, setSubTab] = useState(() => defaultChipKey('hierarchy'));
  const [sortCol, setSortCol] = useState<SortKey>('conversions');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);

  // See platformFilteredChips above — falls back off an already-selected
  // 'dsp' chip so a permission change mid-session can't leave the widget
  // resolved to a chip it just stopped offering.
  const { canViewPlatform } = useReportingPermissions();
  const chipsForTab = useMemo(() => platformFilteredChips(tab, canViewPlatform), [tab, canViewPlatform]);
  const activeSubTab = chipsForTab.some(chip => chip.key === subTab) ? subTab : defaultChipKey(tab);
  const filters = useAppSelector(selectReportingV2Filters);

  const {
    dims: chipDims,
    label: chipLabel,
    idKey: chipIdKey,
    unavailable,
    labelFn: resolvedLabelFn,
    showAdvertiser,
  } = resolve(tab, activeSubTab, chipsForTab);
  // Same GAM-prefix leak as Impressions by Type's Channel chip (flank#4741
  // QA) — this widget resolves the chip through the same `resolve`/CHIPS
  // helper, so it shares the identical mask via maskedChannelLabelFn.
  const chipLabelFn = maskedChannelLabelFn(activeSubTab, canViewPlatform, resolvedLabelFn);
  const splittable = !NOT_TYPE_SPLITTABLE.has(tab);

  const isPublisherInventory = isPublisherInventoryTab(tab, activeSubTab);
  const { dims, label, idKey, labelFn } = resolveConversionsDims(isPublisherInventory, {
    dims: chipDims,
    label: chipLabel,
    idKey: chipIdKey,
    labelFn: chipLabelFn,
  });

  const handleTabChange = (next: string) => {
    setTab(next);
    setSubTab(defaultChipKey(next));
    setSortCol('conversions');
    setPage(0);
  };

  const handleSubTabChange = (next: string) => {
    setSubTab(next);
    setPage(0);
  };

  const handleConvTypeChange = (next: ConversionType) => {
    setConvType(next);
    setPage(0);
  };

  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
    setPage(0);
  };

  // One query per chip: conversion_type rides along as a dimension (where the
  // source table carries it) so every row already says which bucket it's in —
  // no second "delivery baseline" query needed, and also lets `availableTypes`
  // below derive which toggle buttons to offer from the same rows (review,
  // Lucas). Entities with delivery but no row for ANY type simply have no
  // conversion data to report; entities with a row for some OTHER type but
  // not the selected one are surfaced in the "doesn't report this type" note
  // below, rather than shown as a bare 0. `dsp` added only where it can't
  // split rows: campaign/line_item/creative don't repeat across DSPs, the
  // same convention `withEntityDsp` already establishes for Impressions by
  // Type. Channel/Geo/Dimensions tabs' own dims aren't DSP-specific, so this
  // is a no-op there. `advertiser` is the one exception with no platform
  // filter (ribeye-api#2515, Martin: one row per Ribeye advertiser) — passing
  // `filters.dsps` keeps `withEntityDsp` from re-fragmenting that rollup; the
  // conversions attribution hazard that creates is handled per-row below via
  // isAdvertiserRollupNotAdditive, not by splitting the row back apart.
  // platform_count backs that check (Josh: Quantum Fiber's Conversions rollup
  // was flagged wrong on the Bonneville demo-prep call — it sums DV360 and
  // Simpli.fi's independently-attributed conversions with nothing saying so).
  const { rows, totalRows, loading, error, restrictedFields } = useReportWidget('conversions', {
    dimensions: withEntityDsp(splittable ? [...dims, 'conversion_type'] : dims, filters.dsps),
    metrics: ['impressions', 'clicks', 'spend', 'conversions', 'platform_count'],
    limit: 500,
    enabled: !unavailable,
  });
  // A capped request (review, CodeRabbit) can client-sort and present fewer
  // entities than actually exist, with nothing on screen saying so — same
  // disclosure convention reportExport.ts's truncationNote uses.
  const truncated = totalRows > rows.length;

  // See isAttributionRestricted — the widget renders nothing rather than a
  // confusing empty-looking grid (same "no bare 0" principle as the rest of
  // this page).
  const attributionRestricted = isAttributionRestricted(restrictedFields);

  const key = (row: ReportRow): string => String((idKey ? row[idKey] : row[label]) ?? row[label] ?? '');

  // Web is always offered as the baseline so the toggle is never empty before
  // the fetch resolves; Listen-through and Foot traffic only appear once a
  // row actually carries that type (review, Lucas: Foot traffic has had zero
  // rows since the 10-02 Simpli.fi backfill, and Listen-through/AdsWizz was
  // never offered at all despite having rows).
  const availableTypes = useMemo(() => {
    const present = new Set(rows.map(r => r.conversion_type));
    return (['web', 'listen_through', 'foot_traffic'] as const).filter(t => t === 'web' || present.has(t));
  }, [rows]);

  useEffect(() => {
    if (!availableTypes.includes(convType)) setConvType(availableTypes[0] ?? 'web');
  }, [availableTypes, convType]);

  const { visibleRows, notReportingNames } = useMemo(() => {
    if (!splittable) {
      // Combined — not split by type at all on this tab.
      return { visibleRows: rows, notReportingNames: [] as string[] };
    }
    const byKey = new Map<string, ReportRow[]>();
    for (const row of rows) {
      const k = key(row);
      (byKey.get(k) ?? byKey.set(k, []).get(k)!).push(row);
    }
    const selected: ReportRow[] = [];
    const missing: string[] = [];
    for (const [k, group] of byKey) {
      const match = group.find(r => r.conversion_type === convType);
      if (match) selected.push(match);
      else missing.push(labelFn(group[0][label], idKey ? group[0][idKey] : undefined) || k);
    }
    return { visibleRows: selected, notReportingNames: missing.sort() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, convType, splittable]);

  const sorted = useMemo(
    () => sortRows(visibleRows, sortCol, sortDir, { label, idKey, labelFn }),
    [visibleRows, sortCol, sortDir, label, idKey, labelFn],
  );

  const onExportCsv = useExportWidgetCsv('conversions', sorted);

  const { pageRows, pageCount, safePage } = paginateRows(sorted, page, rowsPerPage);

  const handleSort = (col: SortKey) => {
    if (col === sortCol) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortCol(col);
      setSortDir('desc');
    }
    setPage(0);
  };

  const COLS: { key: SortKey; label: string; format: (v: unknown) => string }[] = [
    // CONVERSIONS_LABEL, not a hardcoded string — same qualifier every other
    // renderer of the canonical `conversions` metric carries (conversionsLabel.test.ts).
    { key: 'conversions', label: CONVERSIONS_LABEL, format: num },
    { key: 'conv_rate', label: 'Conv. rate', format: percent },
    { key: 'impressions', label: 'Impressions', format: num },
    { key: 'clicks', label: 'Clicks', format: num },
    { key: 'spend', label: 'Spend', format: compactCurrency },
    { key: 'cost_per_conv', label: 'Cost/conv.', format: currency },
  ];

  const cellValue = (row: ReportRow, col: SortKey): unknown => {
    if (col === 'conv_rate') {
      const c = toNum(row.conversions);
      const i = toNum(row.impressions);
      return c !== null && i ? c / i : null;
    }
    if (col === 'cost_per_conv') {
      const s = toNum(row.spend);
      const c = toNum(row.conversions);
      return s !== null && c ? s / c : null;
    }
    return row[col];
  };

  if (attributionRestricted) return null;

  return (
    <Box className='rv2-card' sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
          Conversions
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            size='small'
            exclusive
            value={convType}
            onChange={(_e, v) => v && handleConvTypeChange(v)}
            sx={darkToggleSx}
            // Geo/Publishers show the same combined rows under either choice
            // (review, CodeRabbit) — disabled rather than left active and
            // silently inert, matching the caption below that already
            // explains why.
            disabled={!splittable}
          >
            {availableTypes.map(t => (
              <ToggleButton key={t} value={t}>
                {CONVERSION_TYPE_LABELS[t]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <WidgetExportButton onExport={onExportCsv} disabled={loading || sorted.length === 0} />
        </Box>
      </Box>

      <ToggleButtonGroup
        size='small'
        exclusive
        value={tab}
        onChange={(_e, v) => v && handleTabChange(v)}
        sx={darkToggleSx}
      >
        {TABS.map(t => (
          <ToggleButton key={t.key} value={t.key}>
            {t.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <ToggleButtonGroup
        size='small'
        exclusive
        value={activeSubTab}
        onChange={(_e, v) => v && handleSubTabChange(v)}
        sx={darkToggleSx}
      >
        {chipsForTab.map(chip =>
          chip.dims ? (
            <ToggleButton key={chip.key} value={chip.key}>
              {chip.label}
            </ToggleButton>
          ) : (
            <Tooltip key={chip.key} title={chip.reason ?? ''} placement='top' arrow>
              <span style={{ display: 'inline-flex' }}>
                <ToggleButton value={chip.key} disabled>
                  {chip.label}
                </ToggleButton>
              </span>
            </Tooltip>
          ),
        )}
      </ToggleButtonGroup>

      {!splittable && (
        <Typography variant='caption' sx={{ color: COLORS.muted }}>
          Not broken down by Web/Foot traffic at this grain — {TABS.find(t => t.key === tab)?.label} doesn't carry
          conversion_type. Switch to Hierarchy, Channel or Dimensions to see the split.
          {tab === 'inventory' &&
            !isPublisherInventory &&
            ' Also showing raw App/Domain rows, not the Publishers rollup.'}
        </Typography>
      )}

      {unavailable ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
          {unavailable}
        </Typography>
      ) : loading ? (
        <WidgetSkeleton rows={10} />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      ) : sorted.length === 0 ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
          No {!splittable ? '' : `${CONVERSION_TYPE_LABELS[convType].toLowerCase()} `}conversions for the selected
          filters.
        </Typography>
      ) : (
        <Box className='rv2-scroll' sx={{ overflowX: 'auto' }}>
          <Table size='small' sx={{ tableLayout: 'fixed', minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ color: COLORS.muted, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                  onClick={() => handleSort('__name')}
                >
                  {TABS.find(t => t.key === tab)?.label ?? 'Name'}{' '}
                  <SortIcon col='__name' sortCol={sortCol} sortDir={sortDir} />
                </TableCell>
                {showAdvertiser && (
                  <TableCell sx={{ color: COLORS.muted, fontWeight: 600, fontSize: 12 }}>Advertiser</TableCell>
                )}
                {COLS.map(col => (
                  <TableCell
                    key={col.key}
                    align='right'
                    sx={{
                      position: 'relative',
                      width: colWidths[col.key] ? `${colWidths[col.key]}px` : undefined,
                      color: COLORS.muted,
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label} <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                    <ColumnResizeHandle columnKey={col.key} onResizeStart={startResize} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row, index) => {
                const displayName = labelFn(row[label], idKey ? row[idKey] : undefined);
                const fullName = fullNameFor(row, label);
                const rowRollupNotAdditive = isAdvertiserRollupNotAdditive(dims, row);
                return (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: COLORS.pageBg } }}>
                    <TableCell sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      <Tooltip title={fullName ?? displayName} arrow placement='top-start'>
                        <Typography
                          component='span'
                          variant='body2'
                          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {displayName}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    {showAdvertiser && (
                      <TableCell sx={{ overflow: 'hidden', whiteSpace: 'nowrap', color: COLORS.muted }}>
                        {dimensionLabel(row.advertiser_name)}
                      </TableCell>
                    )}
                    {COLS.map(col => (
                      <TableCell key={col.key} align='right' sx={{ fontWeight: col.key === sortCol ? 600 : 400 }}>
                        {CONVERSIONS_DERIVED_COLS.has(col.key) && rowRollupNotAdditive ? (
                          <Tooltip title={CONVERSIONS_NOT_ADDITIVE_NOTE} placement='top' arrow>
                            <Typography
                              component='span'
                              variant='inherit'
                              sx={{ color: COLORS.muted, fontStyle: 'italic' }}
                            >
                              {NOT_ADDITIVE_LABEL}
                            </Typography>
                          </Tooltip>
                        ) : (
                          (() => {
                            const v = cellValue(row, col.key);
                            return v === null || v === undefined || v === '' ? '—' : col.format(v);
                          })()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {sorted.length > ROWS_PER_PAGE_OPTIONS[0] && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant='caption' sx={{ color: COLORS.muted }}>
            {(safePage * rowsPerPage + 1).toLocaleString()}–
            {(safePage * rowsPerPage + pageRows.length).toLocaleString()} of {sorted.length.toLocaleString()} rows
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant='caption' sx={{ color: COLORS.muted }}>
                Rows
              </Typography>
              <ToggleButtonGroup
                size='small'
                exclusive
                value={rowsPerPage}
                onChange={(_e, v) => v && handleRowsPerPageChange(v)}
                sx={darkToggleSx}
              >
                {ROWS_PER_PAGE_OPTIONS.map(n => (
                  <ToggleButton key={n} value={n}>
                    {n}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size='small'
                disabled={safePage === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
              >
                Prev
              </Button>
              {pageWindow(safePage, pageCount).map((n, i) =>
                n === -1 ? (
                  <Typography key={`gap-${i}`} variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                    …
                  </Typography>
                ) : (
                  <Button
                    key={n}
                    size='small'
                    onClick={() => setPage(n)}
                    sx={{
                      textTransform: 'none',
                      minWidth: 32,
                      px: 1,
                      color: n === safePage ? '#fff' : COLORS.muted,
                      bgcolor: n === safePage ? COLORS.black : 'transparent',
                      '&:hover': { bgcolor: n === safePage ? '#000' : COLORS.pageBg },
                    }}
                  >
                    {n + 1}
                  </Button>
                ),
              )}
              <Button
                size='small'
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* No Total row here on purpose — conversions across platforms aren't
          deduplicated (flank#4477), so a summed total would double-count. */}

      {notReportingNames.length > 0 && (
        <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block' }}>
          {notReportingNames.length} {notReportingNames.length === 1 ? "entity doesn't" : "entities don't"} report{' '}
          {CONVERSION_TYPE_LABELS[convType].toLowerCase()} conversions, so{' '}
          {notReportingNames.length === 1 ? 'it is' : 'they are'} left out rather than shown as 0:{' '}
          {notReportingNames.slice(0, 10).join(', ')}
          {notReportingNames.length > 10 ? `, and ${notReportingNames.length - 10} more` : ''}.
        </Typography>
      )}

      {truncated && (
        <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block' }}>
          Showing {rows.length.toLocaleString()} of {totalRows.toLocaleString()} entities — narrow your filters to see
          the rest.
        </Typography>
      )}
    </Box>
  );
};

const SortIcon = ({ col, sortCol, sortDir }: { col: SortKey; sortCol: SortKey; sortDir: 'asc' | 'desc' }) => {
  if (col !== sortCol)
    return <UnfoldMoreRounded sx={{ fontSize: 14, color: COLORS.border, verticalAlign: 'middle' }} />;
  return sortDir === 'desc' ? (
    <ArrowDownwardRounded sx={{ fontSize: 14, color: COLORS.primary, verticalAlign: 'middle' }} />
  ) : (
    <ArrowUpwardRounded sx={{ fontSize: 14, color: COLORS.primary, verticalAlign: 'middle' }} />
  );
};

export default Conversions;
